import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { usePartnershipContext } from '../../core/context/PartnershipContext';
import ClientOverviewService, { ClientOverviewDto, PartnershipOverviewDto } from './ClientOverviewService';
import styles from './ClientOverview.module.css';

const overviewService = new ClientOverviewService();

function statusColor(pct: number): string {
  if (pct >= 100) return '#4CAF50';
  if (pct > 0) return '#F26522';
  return 'rgba(255,255,255,0.25)';
}

function statusLabel(pct: number): string {
  if (pct >= 100) return 'Complete';
  if (pct > 0) return 'In Progress';
  return 'Not Started';
}

function PartnershipCard({ p, onViewProject, onConfigure }: {
  p: PartnershipOverviewDto;
  onViewProject: () => void;
  onConfigure: () => void;
}) {
  const color = statusColor(p.progressPct);
  const label = statusLabel(p.progressPct);
  const pct = Math.min(100, Math.max(0, p.progressPct));

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>{p.name}</div>
        <span className={styles.partnerBadge}>
          {p.partnerCount.toLocaleString()} partners
        </span>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${pct || 0}%`, background: pct > 0 ? color : 'transparent' }}
          />
        </div>
        <span className={styles.progressLabel}>
          {p.completedCalcs} / {p.totalCalcs} calcs
        </span>
      </div>

      <div className={styles.cardMeta}>
        <span
          className={styles.statusBadge}
          style={{ color, borderColor: color }}
        >
          {label}
        </span>
        <span className={styles.configCount}>
          {p.calcProfileItemCount} calculations configured
        </span>
      </div>

      <div className={styles.cardActions}>
        <button className={styles.btnPrimary} onClick={onViewProject}>
          View Project
        </button>
        <button className={styles.btnSecondary} onClick={onConfigure}>
          Configure
        </button>
      </div>
    </div>
  );
}

export const ClientOverview: React.FC = () => {
  const navigate = useNavigate();
  const { selectedClient, taxYear, setSelectedPartnership } = usePartnershipContext();
  const [data, setData] = useState<ClientOverviewDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    if (!selectedClient) return;
    setIsLoading(true);
    try {
      const result = await overviewService.getClientOverview(selectedClient.id, taxYear);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedClient, taxYear]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  if (!selectedClient) {
    return (
      <div className={styles.glassWrapper}>
        <div className={styles.emptyState}>Select a client to view the overview.</div>
      </div>
    );
  }

  const totalCalcsConfigured = data
    ? data.partnerships.reduce((s, p) => s + p.calcProfileItemCount, 0)
    : 0;
  const overallPct = data && data.partnerships.length > 0
    ? Math.round(data.partnerships.reduce((s, p) => s + p.progressPct, 0) / data.partnerships.length)
    : 0;

  return (
    <div className={styles.glassWrapper}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          {data?.clientName ?? selectedClient.name} — {taxYear} Overview
        </div>
        {data && (
          <div className={styles.pageSubtitle}>
            {data.totalPartnerships} partnerships · {data.totalPartners.toLocaleString()} total partners
          </div>
        )}
      </div>

      {isLoading ? (
        <div className={styles.loadingState}><Spin /></div>
      ) : !data || data.partnerships.length === 0 ? (
        <div className={styles.emptyState}>No partnerships found for this client.</div>
      ) : (
        <>
          <div className={styles.summaryBar}>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{data.totalPartnerships}</div>
              <div className={styles.statLabel}>Total Partnerships</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{totalCalcsConfigured}</div>
              <div className={styles.statLabel}>Calculations Configured</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statValue}>{overallPct}%</div>
              <div className={styles.statLabel}>Overall Progress</div>
            </div>
          </div>

          <div className={styles.cardsGrid}>
            {data.partnerships.map((p) => (
              <PartnershipCard
                key={p.entityId}
                p={p}
                onViewProject={() => {
                  setSelectedPartnership({ id: String(p.entityId), name: p.name, entityType: 'Partnership' });
                  navigate('/calculations/project');
                }}
                onConfigure={() => {
                  setSelectedPartnership({ id: String(p.entityId), name: p.name, entityType: 'Partnership' });
                  navigate('/configurations/calc-profile');
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
